/*
  Kiwi Ears · contact page (Rene 2026-08-22).

  The page is four separate Shopify sections — ct-hero, ct-routes, ct-form,
  ct-who — so this file is loaded by each of them and has to survive any subset
  of them being present, reordered, or removed in the customizer. Two
  consequences: it runs ONCE however many section tags pull it in, and every
  feature guards on its own elements rather than a shared wrapper.

  Four behaviours, all of them there to save the visitor a step:

  1. ROUTES. Picking a row preselects the form's topic, writes it into the URL
     (?topic=…) so the choice is linkable and survives a reload, and scrolls to
     the form through Lenis so the page does not fight the smooth scroller.
     Focus moves to the first empty field on desktop only — on a phone that
     would summon the keyboard over the content the visitor just chose.

  2. THE CLOCKS. "Within 48 hours, Mon to Fri, GMT+8" means something different
     at 03:00 there, so the page shows the team's local time via
     Intl.DateTimeFormat rather than a hardcoded offset (GMT+8 has no DST today,
     but hardcoding an offset is how a page ends up an hour wrong forever).
     Every [data-kiwi-clock] element describes its own zone and opening times,
     so the hero's pill and the closing card's spec row are independent — either
     can be on the page without the other. Ticks every 30s: a seconds-accurate
     clock is a repaint every second for information nobody needs. Open/closed
     only appears when opening times are configured — we do not invent hours the
     brand has not published.

  3. ERROR FOCUS. A failed post reloads the page with Shopify's errors, and the
     browser lands at the top. Focus goes to the field that failed.

  4. REVEALS. One stagger on the hero, once, gated on reduced motion. Anything
     more on a support page is decoration in front of someone with a problem.
*/
(function () {
  /* Loaded by every contact section, so it must initialise exactly once. */
  if (window.__kiwiContactReady) return;
  window.__kiwiContactReady = true;

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var g = window.gsap;

  /* ---------- 1 · routes ---------- */
  var topic = document.getElementById('sc-topic');
  var write = document.getElementById('sc-write');

  function scrollToWrite() {
    if (!write) return;
    /* Lenis owns the scroll position; window.scrollTo would be overridden on
       the next frame. offset matches the section's scroll-mt. */
    if (window.lenis && !still) window.lenis.scrollTo(write, { offset: -80, duration: 0.9 });
    else write.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
  }

  function selectTopic(value) {
    if (!topic || !value) return false;
    var hit = false;
    for (var i = 0; i < topic.options.length; i++) {
      if (topic.options[i].value === value) { topic.selectedIndex = i; hit = true; break; }
    }
    if (hit) {
      /* the select just changed under the visitor's eyes, in a different part of
         the page — a brief ring is what tells them it happened */
      topic.classList.remove('is-set');
      void topic.offsetWidth;                       /* restart the animation */
      topic.classList.add('is-set');
      topic.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return hit;
  }

  function firstEmptyField() {
    var fields = write ? write.querySelectorAll('.sc-input') : [];
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].tagName !== 'SELECT' && !fields[i].value) return fields[i];
    }
    return null;
  }

  [].slice.call(document.querySelectorAll('.sc-rowlink')).forEach(function (link) {
    link.addEventListener('click', function (e) {
      /* let the browser do its normal thing for cmd/ctrl/middle-click and for
         anything that wants a new tab — the href is a real URL and works */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      var value = link.getAttribute('data-topic') || '';
      if (!selectTopic(value)) return;          /* topic unknown: follow the href */
      e.preventDefault();

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('topic', value);
        url.hash = '';
        window.history.replaceState({}, '', url);
      } catch (err) { /* old browser: the scroll still works, the URL just is not updated */ }

      scrollToWrite();
      /* desktop only, and only into a field that is actually empty */
      if (window.matchMedia && window.matchMedia('(min-width: 1024px)').matches) {
        var field = firstEmptyField();
        if (field) setTimeout(function () { field.focus({ preventScroll: true }); }, 620);
      }
    });
  });

  /* arriving with ?topic=… (a shared link, or a failed post bouncing back) */
  (function fromUrl() {
    if (!topic) return;
    try {
      var value = new URL(window.location.href).searchParams.get('topic');
      if (value) selectTopic(value);
    } catch (e) { /* no URL API: nothing to restore */ }
  })();

  /* ---------- 2 · the clocks ---------- */
  (function clocks() {
    var hosts = [].slice.call(document.querySelectorAll('[data-kiwi-clock]'));
    if (!hosts.length) return;

    function minutesOf(text) {
      var bits = String(text).split(':');
      var h = parseInt(bits[0], 10);
      var m = parseInt(bits[1] || '0', 10);
      if (isNaN(h)) return null;
      return h * 60 + (isNaN(m) ? 0 : m);
    }

    function arm(host) {
      var out = host.querySelector('[data-clock-time]');
      if (!out) return null;
      var dot = host.querySelector('[data-clock-dot]');
      var reveal = host.getAttribute('data-reveal') || 'flex';
      var tz = host.getAttribute('data-tz') || 'Asia/Shanghai';
      var span = (host.getAttribute('data-open') || '').trim();

      var fmt, parts;
      try {
        fmt = new Intl.DateTimeFormat([], { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false });
      } catch (e) {
        return null;   /* unknown zone: show nothing rather than the wrong time */
      }
      /* weekday and hour in THAT zone, for the open/closed test */
      try {
        parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false });
      } catch (e) { parts = null; }

      var open = null, close = null;
      if (span.indexOf('-') > -1) {
        var ends = span.split('-');
        open = minutesOf(ends[0]);
        close = minutesOf(ends[1]);
        if (open === null || close === null) { open = null; close = null; }
      }

      return function tick() {
        var now = new Date();
        out.textContent = fmt.format(now);

        /* only revealed once it holds a real value */
        host.hidden = false;
        host.classList.remove('hidden');
        host.classList.add(reveal);

        if (open === null || !parts) {
          host.removeAttribute('title');
          return;
        }
        /* weekday + minutes-since-midnight, read back from the formatter so the
           comparison happens in the team's zone, not the visitor's */
        var day = '', hour = 0, minute = 0;
        parts.formatToParts(now).forEach(function (p) {
          if (p.type === 'weekday') day = p.value;
          if (p.type === 'hour') hour = parseInt(p.value, 10);
          if (p.type === 'minute') minute = parseInt(p.value, 10);
        });
        var weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].indexOf(day) > -1;
        var mins = hour * 60 + minute;
        var isOpen = weekday && mins >= open && mins < close;

        if (dot) dot.className = 'h-1.5 w-1.5 rounded-full ' + (isOpen ? 'bg-kiwi' : 'bg-white/40');
        host.setAttribute('title', isOpen ? 'Someone is at their desk right now' : 'Outside working hours — messages are queued');
      };
    }

    var ticks = hosts.map(arm).filter(Boolean);
    if (!ticks.length) return;
    function run() { ticks.forEach(function (t) { t(); }); }

    run();
    /* 30s, and only while the tab is visible */
    var timer = setInterval(run, 30000);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { clearInterval(timer); timer = null; }
      else if (!timer) { run(); timer = setInterval(run, 30000); }
    });
  })();

  /* ---------- 4 · sending, and not losing what was typed ---------- */
  /* Every form marked .kiwi-form, not one hardcoded id: the contact form and the
     trade-in quote form want identical behaviour, and the next one will too. */
  [].slice.call(document.querySelectorAll('form.kiwi-form')).forEach(function (form) {
    var button = form.querySelector('.sc-submit');
    var label = form.querySelector('.sc-submitlabel');
    var message = form.querySelector('textarea.sc-input');
    var sending = false;

    form.addEventListener('submit', function () {
      sending = true;                      /* a real post: the draft is on its way, not lost */
      if (!button) return;
      /* Disable AFTER the submit event, or the browser drops the button's own
         form participation and some browsers cancel the post outright. */
      setTimeout(function () {
        button.disabled = true;
        if (label && button.getAttribute('data-sending')) label.textContent = button.getAttribute('data-sending');
      }, 0);
    });

    /* A half-written fault report — or a description of the pair someone wants to
       trade — is expensive to retype, so a stray click on a nav link asks first.
       Only for the message field; nobody minds losing a half-typed name. */
    window.addEventListener('beforeunload', function (e) {
      if (sending || !message || !message.value.trim()) return;
      e.preventDefault();
      e.returnValue = '';                  /* the wording is the browser's, not ours */
      return '';
    });

    /* A failed post reloads with Shopify's errors and the browser lands at the
       top; focus goes to the field that failed. */
    if (form.querySelector('[role="alert"]')) {
      var bad = form.querySelector('.sc-input:invalid') || form.querySelector('.sc-input');
      if (bad) setTimeout(function () { bad.focus({ preventScroll: false }); }, 60);
    }
  });

  /* ---------- 5 · one reveal ---------- */
  (function reveal() {
    var items = [].slice.call(document.querySelectorAll('.sc-rise'));
    if (!items.length) return;
    if (!g || still) { items.forEach(function (el) { el.style.opacity = ''; }); return; }
    var mm = g.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', function () {
      g.set(items, { opacity: 0, y: 18 });
      g.to(items, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.08, delay: 0.05, clearProps: 'transform,opacity' });
    });
  })();
})();
